import type { EntityOrSuggestionRef } from '@bliks-io/contracts';

/**
 * Read the resolved entity id out of an `EntityOrSuggestionRef`. Returns
 * `undefined` for suggestion refs (they don't name a resolved entity yet) or
 * for missing input — callers that need a resolved id should branch on the
 * absence of the result.
 */
export function getEntityRefId(ref: EntityOrSuggestionRef | null | undefined): string | undefined {
  if (!ref) return undefined;
  return ref.type === 'entity' ? ref.id : undefined;
}

/**
 * Filter an array of refs down to resolved entity ids, dropping suggestion
 * entries. Use when a downstream API only understands resolved entities
 * (pickers, lookups, exclude lists).
 */
export function getEntityRefIds(
  refs: ReadonlyArray<EntityOrSuggestionRef> | null | undefined
): string[] {
  if (!refs) return [];
  const out: string[] = [];
  for (const ref of refs) {
    if (ref.type === 'entity') out.push(ref.id);
  }
  return out;
}

/**
 * Split one ref slot into its resolved entity ids and its pending suggestion
 * ids in a single pass. Use when a surface needs BOTH halves — resolved ids for
 * entity lookups, suggestion ids for ghost rows and pre-checking.
 */
export function partitionEntityRefs(
  refs: ReadonlyArray<EntityOrSuggestionRef> | null | undefined
): {
  entityIds: string[];
  suggestionIds: string[];
} {
  const entityIds: string[] = [];
  const suggestionIds: string[] = [];
  for (const ref of refs ?? []) {
    if (ref.type === 'entity') entityIds.push(ref.id);
    else suggestionIds.push(ref.id);
  }
  return { entityIds, suggestionIds };
}
